import React from "react";
import { Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

import { splitApiRequestFormErrors } from "../../api";
import type { Recording } from "../../api/types";

type RecordingNameEditorVariant = "list" | "detail";

type RecordingNameEditorProps = {
  recording: Pick<Recording, "id" | "name">;
  title: string;
  onRename?: (id: string, name: string | null) => Promise<void>;
  variant?: RecordingNameEditorVariant;
};

const RecordingNameEditor = ({
  recording,
  title,
  onRename,
  variant = "list",
}: RecordingNameEditorProps) => {
  const { t } = useTranslation();
  const [editingValue, setEditingValue] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const skipCommit = React.useRef(false);
  const isDetail = variant === "detail";

  const stopRowNavigation = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const beginRename = React.useCallback(() => {
    setEditingValue(recording.name ?? title);
    setError(null);
  }, [recording.name, title]);

  const startRename = (event: React.MouseEvent) => {
    stopRowNavigation(event);
    beginRename();
  };

  const commitRename = async (value: string) => {
    if (!onRename) return;

    const trimmed = value.trim();
    if (trimmed === (recording.name ?? title).trim()) {
      setEditingValue(null);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onRename(recording.id, trimmed === "" ? null : trimmed);
      setEditingValue(null);
    } catch (error: unknown) {
      const parsed = splitApiRequestFormErrors<{ name: string }>(error, t, ["name"]);
      setError(parsed.fieldErrors.name ?? parsed.remainder);
    } finally {
      setSaving(false);
    }
  };

  const isEditing = editingValue !== null;
  const wrapperClassName = isDetail
    ? "flex min-w-0 items-center gap-2"
    : "flex min-w-0 items-center gap-1";
  const titleClassName = isDetail
    ? "min-w-0 max-w-full truncate text-2xl font-bold text-text font-display"
    : "min-w-0 max-w-full truncate text-sm font-medium text-text";
  const inputClassName = isDetail
    ? "relative z-20 h-9 min-w-0 max-w-full flex-1 rounded border border-outline bg-surface px-2 text-2xl font-bold text-text font-display outline-none focus:border-info focus:ring-1 focus:ring-info/30"
    : "relative z-20 h-6 min-w-0 max-w-full flex-1 rounded border border-outline bg-surface px-1.5 text-sm font-medium text-text outline-none focus:border-info focus:ring-1 focus:ring-info/30";
  const buttonClassName =
    "relative z-20 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-text-dim transition hover:bg-muted-surface hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/30 disabled:opacity-50";
  const errorClassName = isDetail
    ? "mt-1 truncate text-sm text-danger-strong"
    : "mt-1 truncate text-xs text-danger-strong";

  return (
    <>
      <div className={wrapperClassName}>
        {isEditing ? (
          <input
            autoFocus
            value={editingValue}
            disabled={saving}
            onClick={stopRowNavigation}
            onFocus={event => event.currentTarget.select()}
            onChange={event => setEditingValue(event.target.value)}
            onBlur={event => {
              if (skipCommit.current) {
                skipCommit.current = false;
                return;
              }
              void commitRename(event.currentTarget.value);
            }}
            onKeyDown={event => {
              event.stopPropagation();
              if (event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.blur();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                skipCommit.current = true;
                setEditingValue(null);
              }
            }}
            className={inputClassName}
            aria-label="Recording name"
          />
        ) : isDetail ? (
          <h1 className={titleClassName}>{title}</h1>
        ) : onRename ? (
          <button
            type="button"
            disabled={saving}
            onClick={startRename}
            className={`${titleClassName} relative z-20 cursor-text rounded text-left underline-offset-2 decoration-dotted hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/30`}
            title={t("rename_recording")}
          >
            {title}
          </button>
        ) : (
          <span className={titleClassName}>{title}</span>
        )}
        {onRename && !isEditing && isDetail && (
          <button
            type="button"
            disabled={saving}
            onClick={startRename}
            className={buttonClassName}
            title={t("rename_recording")}
            aria-label={t("rename_recording")}
          >
            <Pencil size={16} />
          </button>
        )}
      </div>
      {error && <div className={errorClassName}>{error}</div>}
    </>
  );
};

export default RecordingNameEditor;
